import React, { useCallback, useMemo } from "react"
import { makeStyles } from "tss-react/mui"
import { Box, Grid, Stack } from "@mui/material"
import { MRT_ColumnDef, MRT_FullScreenToggleButton } from "material-react-table"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { BalancePaymentDetails, ParsedPaymentDetails, PaymentForPeriod } from "../../supplierAcquiringReportsTypes"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../../localization/useDateTime"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    TableNumberOfItemsBox,
    TableSummaryWrap,
    TableTotalAmountBox,
} from "../../../../../../shared/components/table/TableSummaryBox"
import {
    fetchPaymentsForPeriod,
    FetchPaymentsForPeriodResponse,
    SupplierAcquiringReportsQuery,
    SupplierBalanceDetailsResponse,
} from "../../supplierAcquiringReportsApi"
import { useSupplier } from "../../../../supplierHooks"
import { useExportAcquiringReportsBankTransfersDetailsToCsv } from "../../hooks/useExportAcquiringReportsBankTransfersDetailsToCsv"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { StandardToggleGlobalFilterButton } from "../../../../../../shared/components/table/StandardToggleGlobalFilterButton"
import { toDineroOrUndefined } from "../../../../../currency/currencyLib"
import { subtract } from "dinero.js"

type TransferDetailsDialogProps = Readonly<{
    open: boolean
    onClose: () => void
    fromDateTime?: string
    toDateTime?: string
}>

const useStyles = makeStyles()(() => ({
    container: {
        margin: "0 auto",
    },
    wrap: {
        overflow: "auto",
    },
    tablePaper: {
        boxShadow: "none",
    },
}))

export function TransferDetailsDialog({ open, onClose, fromDateTime, toDateTime }: TransferDetailsDialogProps) {
    const { classes } = useStyles()
    const { supplierId } = useSupplier()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()

    const selectAndParsePaymentDetails = useCallback(
        (response: SupplierBalanceDetailsResponse) => {
            const parsedPayments: ParsedPaymentDetails[] = response.payments.map((payment: BalancePaymentDetails) => {
                const grossAmount = toDineroOrUndefined(payment.grossAmount)
                const feeAmount = toDineroOrUndefined(payment.feeAmount)

                // API returns wrong net amount, so we calculate client-side
                const netAmount =
                    grossAmount && feeAmount ? subtract(grossAmount, feeAmount) : toDineroOrUndefined(payment.netAmount)

                return {
                    ...payment,
                    bookingDate: dateTimeFactory.fromApi(payment.bookingDate),
                    valueDate: dateTimeFactory.fromApiOrUndefined(payment.valueDate),
                    grossAmount,
                    feeAmount,
                    netAmount,
                    effectiveAmount: undefined,
                    type: undefined,
                }
            })

            return parsedPayments
        },
        [dateTimeFactory],
    )

    const paymentsForPeriod = useApiQuery(fetchPaymentsForPeriod, {
        queryName: SupplierAcquiringReportsQuery.PAYMENTS_FOR_PERIOD,
        dependencies: {
            supplierId,
            fromDateTime: fromDateTime ?? "",
            toDateTime: toDateTime ?? "",
        },
        select: selectAndParsePaymentDetails,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(paymentsForPeriod)

    const columns = useMemo<MRT_ColumnDef<ParsedPaymentDetails>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("supplier-acquiring-bank-transfers-table-details-table-order-id-column"),
                accessorKey: "orderId",
            }),
            columnFactory.nameColumn({
                header: l10n.getString("supplier-acquiring-bank-transfers-table-details-table-psp-reference-column"),
                accessorKey: "pspPaymentReference",
            }),
            columnFactory.parsedDateTimeColumn(
                {
                    id: "bookingDate",
                    header: l10n.getString("supplier-acquiring-bank-transfers-table-details-table-booked-column"),
                },
                (row) => row.bookingDate,
            ),
            columnFactory.parsedDateTimeColumn(
                {
                    id: "valueDate",
                    header: l10n.getString("supplier-acquiring-bank-transfers-table-details-table-available-column"),
                },
                (row) => row.valueDate,
            ),
            columnFactory.parsedMoneyColumn(
                {
                    id: "grossAmount",
                    header: l10n.getString("supplier-acquiring-bank-transfers-table-details-table-gross-column"),
                },
                (row) => row.grossAmount,
                true,
            ),
            columnFactory.parsedMoneyColumn(
                {
                    id: "feeAmount",
                    header: l10n.getString("supplier-acquiring-bank-transfers-table-details-table-fee-column"),
                },
                (row) => row.feeAmount,
                true,
            ),
            columnFactory.parsedMoneyColumn(
                {
                    id: "netAmount",
                    header: l10n.getString("supplier-acquiring-bank-transfers-table-details-table-net-column"),
                },
                (row) => row.netAmount,
                true,
            ),
        ],
        [columnFactory, l10n],
    )

    const handleExportRows = useExportAcquiringReportsBankTransfersDetailsToCsv(
        paymentsForPeriod.data ?? [],
        fromDateTime ?? "",
        toDateTime ?? "",
    )

    return (
        <LocalizedStrict
            id="supplier-acquiring-bank-transfers-details-dialog-title"
            attrs={{ dialogTitle: true, subtitle: true }}
            vars={{
                periodStart: fromDateTime
                    ? dateTimeFactory.formatDate(dateTimeFactory.fromApi(fromDateTime))
                    : l10n.getString("supplier-acquiring-bank-transfers-details-dialog-title-no-start-date"),
                periodEnd: dateTimeFactory.formatDate(dateTimeFactory.fromApi(toDateTime ?? "")),
            }}
        >
            <StandardDialog
                open={open}
                onClose={onClose}
                dialogTitle="Transfer details"
                subtitle={`Period: ${fromDateTime} - ${toDateTime}`}
                maxWidthProp="xl"
                isFullScreen
            >
                <Box className={classes.container}>
                    <Grid container direction="column" className={classes.wrap}>
                        <ApiQueryTable
                            columns={columns}
                            initialState={{
                                density: "spacious",
                                sorting: [
                                    {
                                        id: "bookingDate",
                                        desc: false,
                                    },
                                ],
                            }}
                            enableRowActions={false}
                            renderToolbarInternalActions={({ table }) => (
                                <Stack direction="row" spacing={1}>
                                    <StandardExportButton onClick={() => handleExportRows()} />
                                    <StandardToggleGlobalFilterButton table={table} />
                                    <MRT_FullScreenToggleButton table={table} />
                                </Stack>
                            )}
                            renderBottomToolbarCustomActions={({ table }) => (
                                <TableSummaryWrap>
                                    <LocalizedStrict
                                        id="supplier-acquiring-bank-transfers-details-dialog-number-of-orders"
                                        attrs={{ title: true }}
                                    >
                                        <TableNumberOfItemsBox
                                            data={table.getFilteredRowModel().rows}
                                            title="Number of orders"
                                        />
                                    </LocalizedStrict>

                                    <TableTotalAmountBox
                                        data={table.getFilteredRowModel().rows}
                                        accessor={(row) => row.original.grossAmount}
                                        localizedKey="supplier-acquiring-bank-transfers-details-dialog-table-gross-total"
                                    />

                                    <TableTotalAmountBox
                                        data={table.getFilteredRowModel().rows}
                                        accessor={(row) => row.original.feeAmount}
                                        localizedKey="supplier-acquiring-bank-transfers-details-dialog-table-fee-total"
                                    />

                                    <TableTotalAmountBox
                                        data={table.getFilteredRowModel().rows}
                                        accessor={(row) => row.original.netAmount}
                                        localizedKey="supplier-acquiring-bank-transfers-details-dialog-table-net-total"
                                    />
                                </TableSummaryWrap>
                            )}
                            muiTablePaperProps={{ className: classes.tablePaper }}
                        />
                    </Grid>
                </Box>
            </StandardDialog>
        </LocalizedStrict>
    )
}
